import { useCallback, useEffect, useState } from "react";
import "./App.css";
import {
  INITIAL_DEPARTMENT_FORM,
  INITIAL_DESIGNATION_FORM,
  INITIAL_EMPLOYEE_FORM,
  INITIAL_FILE_STATE,
  INITIAL_LOGIN_FORM,
  PAGE_SIZE,
  SIDEBAR_ITEMS,
} from "./assets/instateamMasterConfig";
import {
  getCurrentAdmin,
  createDepartment,
  createDesignation,
  createEmployee,
  getEmployeeById,
  listDepartments,
  listDesignations,
  listEmployees,
  loginAdmin,
  updateDepartment,
  updateDepartmentStatus,
  updateDesignation,
  updateDesignationStatus,
  updateEmployee,
  updateEmployeePassword,
  updateEmployeeStatus,
} from "./api/instateamAdminApi";
import { Header, Sidebar, StatusBanner } from "./components/InstateamMasterComponents";
import AddEmployeePage from "./pages/AddEmployeePage";
import AdminDashboardPage from "./pages/AdminDashboardPage";
import AttendancePage from "./pages/AttendancePage";
import AdminLoginPage from "./pages/AdminLoginPage";
import AdminChatPage from "./pages/AdminChatPage";
import AnouncementsPage from "./pages/AnouncementsPage";
import AdminProfilePage from "./pages/AdminProfilePage";
import AdminSettingsPage from "./pages/AdminSettingsPage";
import ComplaintsPage from "./pages/ComplaintsPage";
import DepartmentsPage from "./pages/DepartmentsPage";
import DesignationsPage from "./pages/DesignationsPage";
import DocumentsPage from "./pages/DocumentsPage";
import EmployeeDetailsPage from "./pages/EmployeeDetailsPage";
import EmployeesPage from "./pages/EmployeesPage";
import LeavesPage from "./pages/LeavesPage";
import PayrollPage from "./pages/PayrollPage";
import AdminEmployeePerformancePage from "./pages/AdminEmployeePerformancePage";
import ReportsPage from "./pages/ReportsPage";
import TasksPage from "./pages/TasksPage";
import NotificationsPage from "./pages/NotificationsPage";
import {
  getNotifications as getAdminNotifications,
  markNotificationCategoryRead,
} from "./api/instateamNotificationsApi";
import {
  buildEmployeeAddress,
  applyAdminTheme,
  createDepartmentFormFromDepartment,
  createDesignationFormFromDesignation,
  createEmployeeFormFromEmployee,
  currencyFormatter,
  isThisMonth,
  parseStoredSession,
  STORAGE_KEY,
} from "./utils/adminHelpers";
import {
  canAccessAdminFeature,
  filterAccessibleSidebarItems,
  getFirstAccessibleAdminView,
  normalizeAdminPermissions,
} from "./utils/adminPermissions";

const INITIAL_PASSWORD_FORM = {
  newPassword: "",
  confirmPassword: "",
};

const ADMIN_VIEW_STORAGE_KEY = "instateam-master-active-view";
const ADMIN_PERSISTED_VIEWS = new Set(SIDEBAR_ITEMS.map((item) => item.key));
const MOBILE_RESTRICTION_WIDTH = 992;

function shouldRestrictToDesktop() {
  if (typeof window === "undefined") return false;

  const nav = window.navigator || {};
  const ua = String(nav.userAgent || "").toLowerCase();
  const uaMobile =
    Boolean(nav.userAgentData?.mobile) ||
    /android|iphone|ipad|ipod|mobile|iemobile|opera mini|blackberry/i.test(ua);
  const viewportWidth = Number(window.innerWidth || 0);
  const screenWidth = Number(window.screen?.width || viewportWidth || 0);
  const screenHeight = Number(window.screen?.height || window.innerHeight || 0);
  const smallScreen = Math.min(screenWidth, screenHeight || screenWidth) <= MOBILE_RESTRICTION_WIDTH;
  const touchDevice = Number(nav.maxTouchPoints || 0) > 0;
  const coarsePointer = window.matchMedia?.("(pointer: coarse)")?.matches ?? false;
  const noHover = window.matchMedia?.("(hover: none)")?.matches ?? false;

  return (
    uaMobile ||
    viewportWidth <= MOBILE_RESTRICTION_WIDTH ||
    (touchDevice && coarsePointer && smallScreen) ||
    (touchDevice && noHover && smallScreen)
  );
}

function DeviceRestrictedScreen() {
  return (
    <div className="device-restricted-screen">
      <section className="device-restricted-card" role="dialog" aria-modal="true">
        <div className="device-restricted-badge">PC</div>
        <h1>Restricted In Mobile</h1>
        <p>
          Insta Team admin panel is designed for laptop and desktop only.
          Open it on a PC or laptop to continue.
        </p>
        <small>Mobile browsers and desktop-site mode on mobile are not supported.</small>
      </section>
    </div>
  );
}

function buildSidebarNotificationCounts(payload = {}) {
  const counts = { ...(payload.counts || {}) };
  (payload.notifications || []).forEach((notification) => {
    if (!notification?.unread || !notification.category) return;
    const key = `${notification.category}Unread`;
    counts[key] = Math.max(Number(counts[key] || 0), 1);
  });
  return counts;
}

function getStoredAdminView(session) {
  if (!session?.token) return "login";
  const storedView = window.localStorage.getItem(ADMIN_VIEW_STORAGE_KEY);
  if (storedView && ADMIN_PERSISTED_VIEWS.has(storedView)) {
    return storedView;
  }
  return getFirstAccessibleAdminView(session?.admin?.permissions, "employees");
}

function DesktopApp() {
  const initialSession = parseStoredSession();
  const [session, setSession] = useState(initialSession);
  const [view, setView] = useState(() => getStoredAdminView(initialSession));

  const [loginForm, setLoginForm] = useState(INITIAL_LOGIN_FORM);

  const [employeeForm, setEmployeeForm] = useState(INITIAL_EMPLOYEE_FORM);
  const [employeeFiles, setEmployeeFiles] = useState(INITIAL_FILE_STATE);
  const [passwordForm, setPasswordForm] = useState(INITIAL_PASSWORD_FORM);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [isEditingEmployee, setIsEditingEmployee] = useState(false);

  const [departmentForm, setDepartmentForm] = useState(INITIAL_DEPARTMENT_FORM);
  const [editingDepartment, setEditingDepartment] = useState(null);
  const [isDepartmentModalOpen, setIsDepartmentModalOpen] = useState(false);

  const [designationForm, setDesignationForm] = useState(INITIAL_DESIGNATION_FORM);
  const [editingDesignation, setEditingDesignation] = useState(null);
  const [isDesignationModalOpen, setIsDesignationModalOpen] = useState(false);

  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);

  const [employeeSearchText, setEmployeeSearchText] = useState("");
  const [dashboardSearchText, setDashboardSearchText] = useState("");
  const [employeeDepartmentFilter, setEmployeeDepartmentFilter] = useState("all");
  const [employeeCurrentPage, setEmployeeCurrentPage] = useState(1);

  const [departmentSearchText, setDepartmentSearchText] = useState("");
  const [departmentStatusFilter, setDepartmentStatusFilter] = useState("all");
  const [departmentCurrentPage, setDepartmentCurrentPage] = useState(1);
  const [departmentPageSize, setDepartmentPageSize] = useState(10);

  const [designationSearchText, setDesignationSearchText] = useState("");
  const [designationDepartmentFilter, setDesignationDepartmentFilter] = useState("all");
  const [designationStatusFilter, setDesignationStatusFilter] = useState("all");
  const [designationCurrentPage, setDesignationCurrentPage] = useState(1);
  const [designationPageSize, setDesignationPageSize] = useState(10);
  const [payrollSearchText, setPayrollSearchText] = useState("");
  const [leaveSearchText, setLeaveSearchText] = useState("");
  const [taskSearchText, setTaskSearchText] = useState("");
  const [announcementSearchText, setAnnouncementSearchText] = useState("");
  const [complaintSearchText, setComplaintSearchText] = useState("");
  const [chatSearchText, setChatSearchText] = useState("");
  const [attendanceSearchText, setAttendanceSearchText] = useState("");
  const [documentSearchText, setDocumentSearchText] = useState("");
  const [reportSearchText, setReportSearchText] = useState("");
  const [selectedPerformanceEmployee, setSelectedPerformanceEmployee] = useState(null);
  const [selectedPerformanceFilters, setSelectedPerformanceFilters] = useState({
    range: "Week",
    month: "",
  });
  const [notificationCount, setNotificationCount] = useState(0);
  const [notificationCounts, setNotificationCounts] = useState({});
  const [notificationMode, setNotificationMode] = useState("list");
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  const [statusMessage, setStatusMessage] = useState(null);
  const [isSubmittingLogin, setIsSubmittingLogin] = useState(false);
  const [isLoadingEmployees, setIsLoadingEmployees] = useState(false);
  const [isLoadingEmployeeDetails, setIsLoadingEmployeeDetails] = useState(false);
  const [isSavingEmployee, setIsSavingEmployee] = useState(false);
  const [isSavingPassword, setIsSavingPassword] = useState(false);
  const [isUpdatingEmployeeStatus, setIsUpdatingEmployeeStatus] = useState(false);
  const [isLoadingDepartments, setIsLoadingDepartments] = useState(false);
  const [isSavingDepartment, setIsSavingDepartment] = useState(false);
  const [isLoadingDesignations, setIsLoadingDesignations] = useState(false);
  const [isSavingDesignation, setIsSavingDesignation] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isLogoutConfirmOpen, setIsLogoutConfirmOpen] = useState(false);

  const persistSession = useCallback((nextSession) => {
    const safeSession = nextSession?.admin
      ? {
          ...nextSession,
          admin: {
            ...nextSession.admin,
            permissions: normalizeAdminPermissions(nextSession.admin.permissions),
          },
        }
      : nextSession;

    if (nextSession) {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(safeSession));
    } else {
      window.localStorage.removeItem(STORAGE_KEY);
      window.localStorage.removeItem(ADMIN_VIEW_STORAGE_KEY);
    }
    setSession(safeSession);
  }, []);

  const fetchNotificationCount = useCallback(async () => {
    if (!session?.token || !canAccessAdminFeature(session?.admin?.permissions, "notifications")) {
      return;
    }
    try {
      const payload = await getAdminNotifications(session.token);
      const nextCounts = buildSidebarNotificationCounts(payload);
      setNotificationCounts(nextCounts);
      setNotificationCount(payload.counts?.unread || 0);
    } catch (error) {
      // Notification count should never block the dashboard.
    }
  }, [session?.admin?.permissions, session?.token]);

  const clearSidebarNotificationDot = useCallback(async (category) => {
    if (!category || !session?.token) return;
    setNotificationCounts((current) => ({ ...current, [`${category}Unread`]: 0 }));
    try {
      const payload = await markNotificationCategoryRead(session.token, category);
      const nextCounts = payload.counts || {};
      setNotificationCounts(nextCounts);
      setNotificationCount(nextCounts.unread || 0);
    } catch (error) {
      fetchNotificationCount();
    }
  }, [fetchNotificationCount, session?.token]);

  useEffect(() => {
    applyAdminTheme(session?.admin?.settings || {});
  }, [session?.admin?.settings]);

  useEffect(() => {
    if (!session?.token) return;
    if (ADMIN_PERSISTED_VIEWS.has(view)) {
      window.localStorage.setItem(ADMIN_VIEW_STORAGE_KEY, view);
    }
  }, [session?.token, view]);

  useEffect(() => {
    if (!session?.token) return undefined;
    fetchNotificationCount();
    const refreshVisibleCount = () => {
      if (!document.hidden) fetchNotificationCount();
    };
    const intervalId = window.setInterval(refreshVisibleCount, 3000);
    window.addEventListener("focus", refreshVisibleCount);
    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("focus", refreshVisibleCount);
    };
  }, [session?.token, fetchNotificationCount]);

  const resetEmployeeForm = useCallback(() => {
    setEmployeeForm(INITIAL_EMPLOYEE_FORM);
    setEmployeeFiles(INITIAL_FILE_STATE);
  }, []);

  const resetPasswordForm = useCallback(() => {
    setPasswordForm(INITIAL_PASSWORD_FORM);
  }, []);

  const clearSelectedEmployee = useCallback(() => {
    setSelectedEmployeeId(null);
    setSelectedEmployee(null);
    setIsEditingEmployee(false);
    resetPasswordForm();
    resetEmployeeForm();
  }, [resetEmployeeForm, resetPasswordForm]);

  const closeDepartmentModal = useCallback(() => {
    setIsDepartmentModalOpen(false);
    setEditingDepartment(null);
    setDepartmentForm(INITIAL_DEPARTMENT_FORM);
  }, []);

  const closeDesignationModal = useCallback(() => {
    setIsDesignationModalOpen(false);
    setEditingDesignation(null);
    setDesignationForm(INITIAL_DESIGNATION_FORM);
  }, []);

  const resetAdminShell = useCallback(() => {
    setEmployees([]);
    setDepartments([]);
    setDesignations([]);
    clearSelectedEmployee();
    closeDepartmentModal();
    closeDesignationModal();
    setNotificationCount(0);
    setNotificationCounts({});
    setSelectedPerformanceEmployee(null);
    setView("login");
    setLoginForm(INITIAL_LOGIN_FORM);
  }, [clearSelectedEmployee, closeDepartmentModal, closeDesignationModal]);

  const handleProtectedError = useCallback((error) => {
    if (error.status === 401 || String(error.message || "").toLowerCase().includes("token")) {
      persistSession(null);
      resetAdminShell();
      return true;
    }

    return false;
  }, [persistSession, resetAdminShell]);

  useEffect(() => {
    if (!session?.token) return;

    let isActive = true;

    async function hydrateCurrentAdmin() {
      try {
        const payload = await getCurrentAdmin(session.token);
        if (!isActive) return;

        const nextSession = {
          token: session.token,
          admin: payload.admin,
        };

        persistSession(nextSession);
      } catch (error) {
        if (!isActive) return;

        persistSession(null);
        resetAdminShell();
        setStatusMessage({
          type: "error",
          text: error.message,
        });
      }
    }

    hydrateCurrentAdmin();

    return () => {
      isActive = false;
    };
  }, [persistSession, resetAdminShell, session?.token]);

  useEffect(() => {
    if (!session?.admin || view === "login" || view === "profile") return;

    const viewPermissionKey =
      view === "add-employee" || view === "employee-details"
        ? "employees"
        : view === "notifications"
          ? "notifications"
          : SIDEBAR_ITEMS.find((item) => item.key === view)?.permissionKey || "";

    if (viewPermissionKey && !canAccessAdminFeature(session.admin.permissions, viewPermissionKey)) {
      const nextView = getFirstAccessibleAdminView(session.admin.permissions, "profile");
      window.localStorage.setItem(ADMIN_VIEW_STORAGE_KEY, nextView);
      setView(nextView);
    }
  }, [session?.admin, view]);

  useEffect(() => {
    setEmployeeCurrentPage(1);
  }, [employeeSearchText, employeeDepartmentFilter]);

  useEffect(() => {
    setDepartmentCurrentPage(1);
  }, [departmentSearchText, departmentStatusFilter, departmentPageSize]);

  useEffect(() => {
    setDesignationCurrentPage(1);
  }, [
    designationSearchText,
    designationDepartmentFilter,
    designationStatusFilter,
    designationPageSize,
  ]);

  useEffect(() => {
    if (!session?.token) return;

    let isActive = true;

    async function hydratePortalData() {
      try {
        setIsLoadingEmployees(true);
        setIsLoadingDepartments(true);
        setIsLoadingDesignations(true);

        const [employeesPayload, departmentsPayload, designationsPayload] = await Promise.all([
          listEmployees(session.token),
          listDepartments(session.token),
          listDesignations(session.token),
        ]);

        if (!isActive) return;

        setEmployees(employeesPayload.employees || []);
        setDepartments(departmentsPayload.departments || []);
        setDesignations(designationsPayload.designations || []);
      } catch (error) {
        if (!isActive) return;

        setStatusMessage({
          type: "error",
          text: error.message,
        });

        handleProtectedError(error);
      } finally {
        if (isActive) {
          setIsLoadingEmployees(false);
          setIsLoadingDepartments(false);
          setIsLoadingDesignations(false);
        }
      }
    }

    hydratePortalData();

    return () => {
      isActive = false;
    };
  }, [handleProtectedError, session?.token]);

  const employeeDepartmentOptions = Array.from(
    new Set(
      departments
        .map((department) => department.name)
        .concat(employees.map((employee) => employee.department))
        .filter(Boolean)
    )
  );

  const activeDepartmentOptions = Array.from(
    new Set(
      departments
        .filter((department) => department.status === "active")
        .map((department) => department.name)
        .filter(Boolean)
    )
  );

  const assignableDepartmentOptions = Array.from(
    new Set(
      activeDepartmentOptions
        .concat(employeeForm.department ? [employeeForm.department] : [])
        .filter(Boolean)
    )
  );

  const assignableDesignationOptions = Array.from(
    new Set(
      designations
        .filter((designation) => {
          if (designation.name === employeeForm.designation) return true;
          if (designation.status !== "active") return false;
          if (!employeeForm.department) return true;
          return designation.department === employeeForm.department;
        })
        .map((designation) => designation.name)
        .concat(employeeForm.designation ? [employeeForm.designation] : [])
        .filter(Boolean)
    )
  );

  const designationDepartmentOptions = Array.from(
    new Set(
      departments
        .map((department) => department.name)
        .concat(designations.map((designation) => designation.department))
        .filter(Boolean)
    )
  );

  const filteredEmployees = employees.filter((employee) => {
    const matchesDepartment =
      employeeDepartmentFilter === "all" ||
      employee.department === employeeDepartmentFilter;

    const haystack = [
      employee.employeeId,
      employee.fullName,
      employee.email,
      employee.username,
      employee.designation,
      employee.department,
    ]
      .join(" ")
      .toLowerCase();

    const matchesSearch = haystack.includes(employeeSearchText.trim().toLowerCase());
    return matchesDepartment && matchesSearch;
  });

  const employeeTotalPages = Math.max(
    1,
    Math.ceil(filteredEmployees.length / PAGE_SIZE)
  );
  const safeEmployeePage = Math.min(employeeCurrentPage, employeeTotalPages);
  const paginatedEmployees = filteredEmployees.slice(
    (safeEmployeePage - 1) * PAGE_SIZE,
    safeEmployeePage * PAGE_SIZE
  );

  const filteredDepartments = departments.filter((department) => {
    const matchesStatus =
      departmentStatusFilter === "all" || department.status === departmentStatusFilter;

    const haystack = [
      department.name,
      department.departmentHeadName,
      department.departmentHeadTitle,
      department.description,
    ]
      .join(" ")
      .toLowerCase();

    const matchesSearch = haystack.includes(departmentSearchText.trim().toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const departmentTotalPages = Math.max(
    1,
    Math.ceil(filteredDepartments.length / departmentPageSize)
  );
  const safeDepartmentPage = Math.min(departmentCurrentPage, departmentTotalPages);
  const paginatedDepartments = filteredDepartments.slice(
    (safeDepartmentPage - 1) * departmentPageSize,
    safeDepartmentPage * departmentPageSize
  );

  const filteredDesignations = designations.filter((designation) => {
    const matchesDepartment =
      designationDepartmentFilter === "all" ||
      designation.department === designationDepartmentFilter;

    const matchesStatus =
      designationStatusFilter === "all" || designation.status === designationStatusFilter;

    const haystack = [
      designation.name,
      designation.department,
      designation.level,
      designation.description,
    ]
      .join(" ")
      .toLowerCase();

    const matchesSearch = haystack.includes(designationSearchText.trim().toLowerCase());
    return matchesDepartment && matchesStatus && matchesSearch;
  });

  const designationTotalPages = Math.max(
    1,
    Math.ceil(filteredDesignations.length / designationPageSize)
  );
  const safeDesignationPage = Math.min(
    designationCurrentPage,
    designationTotalPages
  );
  const paginatedDesignations = filteredDesignations.slice(
    (safeDesignationPage - 1) * designationPageSize,
    safeDesignationPage * designationPageSize
  );

  const totalEmployees = employees.length;
  const activeEmployees = employees.filter((employee) => employee.status === "active").length;
  const inactiveEmployees = employees.filter((employee) => employee.status === "inactive").length;
  const newJoinings = employees.filter((employee) => isThisMonth(employee.dateOfJoining)).length;
  const payrollValue = employees.reduce(
    (sum, employee) => sum + Number(employee.basicSalary || 0),
    0
  );

  const departmentMetrics = {
    totalDepartments: departments.length,
    totalEmployees: departments.reduce(
      (sum, department) => sum + Number(department.employeeCount || 0),
      0
    ),
    activeDepartments: departments.filter((department) => department.status === "active").length,
    inactiveDepartments: departments.filter((department) => department.status === "inactive").length,
  };

  const designationMetrics = {
    totalDesignations: designations.length,
    activeDesignations: designations.filter((designation) => designation.status === "active")
      .length,
    inactiveDesignations: designations.filter(
      (designation) => designation.status === "inactive"
    ).length,
    employeesInDesignations: designations.reduce(
      (sum, designation) => sum + Number(designation.employeeCount || 0),
      0
    ),
  };

  const accessibleSidebarItems = filterAccessibleSidebarItems(
    session?.admin?.permissions
  );

  const activeSidebarKey =
    view === "add-employee" || view === "employee-details"
      ? "employees"
      : SIDEBAR_ITEMS.some((item) => item.key === view)
        ? view
        : "";

  const headerTitleMap = {
    dashboard: "Dashboard",
    employees: "Employees",
    "add-employee": "Add Employee",
    profile: "My Profile",
    departments: "Departments",
    designations: "Designations",
    tasks: "Tasks",
    attendance: "Attendance",
    leaves: "Leaves",
    payroll: "Payroll Management",
    chat: "Chat",
    complaints: "Complaints Management",
    announcements: "Announcements",
    documents: "Documents",
    reports: "Reports",
    settings: "Settings",
  };

  const headerSubtitleMap = {
    dashboard: "Dashboard > Overview",
    employees: "Dashboard > Employees",
    "add-employee": "Dashboard > Employees > Add Employee",
    profile: "Dashboard > My Profile",
    departments: "Dashboard > Departments",
    designations: "Dashboard > Designations",
    tasks: "Dashboard > Tasks",
    attendance: "Dashboard > Attendance",
    leaves: "Dashboard > Leaves",
    payroll: "Dashboard > Payroll",
    chat: "Dashboard > Chat",
    complaints: "Dashboard > Complaints",
    announcements: "Dashboard > Announcements",
    documents: "Dashboard > Documents",
    reports: "Dashboard > Reports",
    settings: "Dashboard > Settings",
  };

  const headerTitle =
    view === "notifications"
      ? notificationMode === "settings"
        ? "Notification Settings"
        : "Notifications"
      : headerTitleMap[view] || selectedEmployee?.fullName || "Employee Details";

  const headerSubtitle =
    view === "notifications"
      ? notificationMode === "settings"
        ? "Dashboard > Notification Settings"
        : "Dashboard > Notifications"
      : headerSubtitleMap[view] ||
        `Dashboard > Employees > ${selectedEmployee?.fullName || "Details"}`;

  const currentSearchText =
    view === "dashboard"
      ? dashboardSearchText
      : view === "departments"
      ? departmentSearchText
      : view === "designations"
        ? designationSearchText
        : view === "payroll"
            ? payrollSearchText
            : view === "attendance"
          ? attendanceSearchText
          : view === "leaves"
            ? leaveSearchText
            : view === "tasks"
              ? taskSearchText
              : view === "chat"
                ? chatSearchText
                : view === "complaints"
                  ? complaintSearchText
                  : view === "announcements"
                    ? announcementSearchText
                    : view === "documents"
                      ? documentSearchText
                      : view === "reports"
                        ? reportSearchText
                    : employeeSearchText;

  const currentSearchPlaceholder =
    view === "dashboard"
      ? "Search employees, departments, documents..."
      : view === "attendance"
      ? "Search attendance by employee, ID, location..."
      : view === "leaves"
      ? "Search leaves by employee, type, status..."
      : view === "documents"
      ? "Search documents by employee, ID, status..."
      : view === "reports"
      ? "Search reports by employee, ID, department..."
      : view === "chat"
      ? "Search employees, chats, messages..."
      : view === "tasks"
        ? "Search tasks, projects..."
      : view === "complaints"
        ? "Search complaints..."
      : view === "announcements"
        ? "Search announcements..."
      : "Search employees...";

  const isHeaderSearchEnabled =
    view === "dashboard" ||
    view === "employees" ||
    view === "departments" ||
    view === "designations" ||
    view === "attendance" ||
    view === "leaves" ||
    view === "payroll" ||
    view === "tasks" ||
    view === "chat" ||
    view === "complaints" ||
    view === "announcements" ||
    view === "documents" ||
    view === "reports";

  function handleHeaderSearchChange(value) {
    if (view === "dashboard") {
      setDashboardSearchText(value);
      return;
    }

    if (view === "departments") {
      setDepartmentSearchText(value);
      return;
    }

    if (view === "designations") {
      setDesignationSearchText(value);
      return;
    }

    if (view === "payroll") {
      setPayrollSearchText(value);
      return;
    }

    if (view === "attendance") {
      setAttendanceSearchText(value);
      return;
    }

    if (view === "leaves") {
      setLeaveSearchText(value);
      return;
    }

    if (view === "tasks") {
      setTaskSearchText(value);
      return;
    }

    if (view === "chat") {
      setChatSearchText(value);
      return;
    }

    if (view === "complaints") {
      setComplaintSearchText(value);
      return;
    }

    if (view === "announcements") {
      setAnnouncementSearchText(value);
      return;
    }

    if (view === "documents") {
      setDocumentSearchText(value);
      return;
    }

    if (view === "reports") {
      setReportSearchText(value);
      return;
    }

    setEmployeeSearchText(value);
  }

  async function refreshEmployees(token = session?.token) {
    if (!token) return [];

    try {
      setIsLoadingEmployees(true);
      const payload = await listEmployees(token);
      const nextEmployees = payload.employees || [];
      setEmployees(nextEmployees);
      return nextEmployees;
    } catch (error) {
      setStatusMessage({
        type: "error",
        text: error.message,
      });
      handleProtectedError(error);
      return [];
    } finally {
      setIsLoadingEmployees(false);
    }
  }

  async function refreshDepartments(token = session?.token) {
    if (!token) return [];

    try {
      setIsLoadingDepartments(true);
      const payload = await listDepartments(token);
      const nextDepartments = payload.departments || [];
      setDepartments(nextDepartments);
      return nextDepartments;
    } catch (error) {
      setStatusMessage({
        type: "error",
        text: error.message,
      });
      handleProtectedError(error);
      return [];
    } finally {
      setIsLoadingDepartments(false);
    }
  }

  async function refreshDesignations(token = session?.token) {
    if (!token) return [];

    try {
      setIsLoadingDesignations(true);
      const payload = await listDesignations(token);
      const nextDesignations = payload.designations || [];
      setDesignations(nextDesignations);
      return nextDesignations;
    } catch (error) {
      setStatusMessage({
        type: "error",
        text: error.message,
      });
      handleProtectedError(error);
      return [];
    } finally {
      setIsLoadingDesignations(false);
    }
  }

  async function fetchEmployeeDetails(employeeId, token = session?.token) {
    if (!employeeId || !token) return null;

    try {
      setIsLoadingEmployeeDetails(true);
      const payload = await getEmployeeById(token, employeeId);
      setSelectedEmployee(payload.employee || null);
      return payload.employee || null;
    } catch (error) {
      setStatusMessage({
        type: "error",
        text: error.message,
      });
      handleProtectedError(error);
      return null;
    } finally {
      setIsLoadingEmployeeDetails(false);
    }
  }

  function buildEmployeeFormData(formState, fileState, includePassword = true) {
    const formData = new FormData();

    Object.entries(formState).forEach(([key, value]) => {
      if (!includePassword && key === "loginPassword") {
        return;
      }

      formData.append(key, value);
    });

    Object.entries(fileState).forEach(([key, file]) => {
      if (file) {
        formData.append(key, file);
      }
    });

    return formData;
  }

  async function openEmployeeDetails(employeeOrId) {
    const employeeId =
      typeof employeeOrId === "string" ? employeeOrId : employeeOrId?._id;

    if (!employeeId || !session?.token) return;

    if (typeof employeeOrId === "object" && employeeOrId) {
      setSelectedEmployee(employeeOrId);
    }

    closeDepartmentModal();
    closeDesignationModal();
    setSelectedEmployeeId(employeeId);
    setView("employee-details");
    setIsEditingEmployee(false);
    resetPasswordForm();
    resetEmployeeForm();

    await fetchEmployeeDetails(employeeId, session.token);
  }

  function openEmployeesView() {
    closeDepartmentModal();
    closeDesignationModal();
    clearSelectedEmployee();
    setView("employees");
  }

  function openDepartmentsView() {
    closeDesignationModal();
    clearSelectedEmployee();
    setView("departments");
  }

  function openDesignationsView() {
    closeDepartmentModal();
    clearSelectedEmployee();
    setView("designations");
  }

  function openAttendanceView() {
    closeDepartmentModal();
    closeDesignationModal();
    clearSelectedEmployee();
    setView("attendance");
  }

  function openLeavesView() {
    closeDepartmentModal();
    closeDesignationModal();
    clearSelectedEmployee();
    setView("leaves");
  }

  function openPayrollView() {
    closeDepartmentModal();
    closeDesignationModal();
    clearSelectedEmployee();
    setView("payroll");
  }

  function openChatView() {
    closeDepartmentModal();
    closeDesignationModal();
    clearSelectedEmployee();
    setView("chat");
  }

  function openEmployeePerformance(employee, filters = {}) {
    if (!employee?.employeeObjectId && !employee?._id) return;
    closeDepartmentModal();
    closeDesignationModal();
    clearSelectedEmployee();
    setSelectedPerformanceEmployee(employee);
    setSelectedPerformanceFilters({
      range: filters.range || "Week",
      month: filters.month || "",
    });
    setView("employee-performance");
  }

  function openProfileView() {
    closeDepartmentModal();
    closeDesignationModal();
    clearSelectedEmployee();
    setView("profile");
  }

  async function handleAdminLogin(event) {
    event.preventDefault();

    try {
      setIsSubmittingLogin(true);
      setStatusMessage(null);

      const payload = await loginAdmin({
        identifier: loginForm.email,
        email: loginForm.email,
        password: loginForm.password,
      });

      persistSession({
        token: payload.token,
        admin: payload.admin,
      });
      const nextView = getFirstAccessibleAdminView(payload.admin?.permissions, "employees");
      window.localStorage.setItem(ADMIN_VIEW_STORAGE_KEY, nextView);
      setView(nextView);
      setStatusMessage({
        type: "success",
        text: "Admin login successful.",
      });
    } catch (error) {
      setStatusMessage({
        type: "error",
        text: error.message,
      });
    } finally {
      setIsSubmittingLogin(false);
    }
  }

  async function handleCreateEmployee(event) {
    event.preventDefault();
    if (!session?.token) return;

    try {
      setIsSavingEmployee(true);
      setStatusMessage(null);

      const payload = await createEmployee(
        session.token,
        buildEmployeeFormData(employeeForm, employeeFiles, true)
      );

      setStatusMessage({
        type: "success",
        text: `Employee saved. Login uses ${payload.loginCredentials.username} / ${payload.loginCredentials.initialPassword}`,
      });
      resetEmployeeForm();
      setView("employees");
      await Promise.all([
        refreshEmployees(session.token),
        refreshDepartments(session.token),
        refreshDesignations(session.token),
      ]);
    } catch (error) {
      setStatusMessage({
        type: "error",
        text: error.message,
      });
      handleProtectedError(error);
    } finally {
      setIsSavingEmployee(false);
    }
  }

  function handleStartEditEmployee() {
    if (!selectedEmployee) return;

    setEmployeeForm(createEmployeeFormFromEmployee(selectedEmployee));
    setEmployeeFiles(INITIAL_FILE_STATE);
    setIsEditingEmployee(true);
  }

  function handleCancelEditEmployee() {
    resetEmployeeForm();
    setEmployeeFiles(INITIAL_FILE_STATE);
    setIsEditingEmployee(false);
  }

  async function handleUpdateEmployee(event) {
    event.preventDefault();
    if (!session?.token || !selectedEmployeeId) return;

    try {
      setIsSavingEmployee(true);
      setStatusMessage(null);

      const payload = await updateEmployee(
        session.token,
        selectedEmployeeId,
        buildEmployeeFormData(employeeForm, employeeFiles, false)
      );

      setSelectedEmployee(payload.employee || null);
      setIsEditingEmployee(false);
      resetEmployeeForm();
      setEmployeeFiles(INITIAL_FILE_STATE);

      setStatusMessage({
        type: "success",
        text: "Employee details updated successfully.",
      });

      await Promise.all([
        refreshEmployees(session.token),
        refreshDepartments(session.token),
        refreshDesignations(session.token),
      ]);
    } catch (error) {
      setStatusMessage({
        type: "error",
        text: error.message,
      });
      handleProtectedError(error);
    } finally {
      setIsSavingEmployee(false);
    }
  }

  async function handlePasswordUpdate(event) {
    event.preventDefault();

    if (!session?.token || !selectedEmployeeId) return;

    if (!passwordForm.newPassword.trim() || !passwordForm.confirmPassword.trim()) {
      setStatusMessage({
        type: "warning",
        text: "Enter and confirm the new password first.",
      });
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setStatusMessage({
        type: "warning",
        text: "New password and confirm password must match.",
      });
      return;
    }

    try {
      setIsSavingPassword(true);
      setStatusMessage(null);

      await updateEmployeePassword(
        session.token,
        selectedEmployeeId,
        passwordForm.newPassword
      );

      resetPasswordForm();
      setStatusMessage({
        type: "success",
        text: "Employee login password updated successfully.",
      });
    } catch (error) {
      setStatusMessage({
        type: "error",
        text: error.message,
      });
      handleProtectedError(error);
    } finally {
      setIsSavingPassword(false);
    }
  }

  async function handleToggleEmployeeStatus() {
    if (!session?.token || !selectedEmployee) return;

    const nextStatus = selectedEmployee.status === "active" ? "inactive" : "active";
    const confirmMessage =
      nextStatus === "inactive"
        ? `Disable ${selectedEmployee.fullName}? They will not be able to access the employee panel until you enable them again.`
        : `Enable ${selectedEmployee.fullName}? This restores employee panel access.`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setIsUpdatingEmployeeStatus(true);
      setStatusMessage(null);

      const payload = await updateEmployeeStatus(
        session.token,
        selectedEmployee._id,
        nextStatus
      );

      setSelectedEmployee(payload.employee || null);
      setStatusMessage({
        type: "success",
        text: payload.message || "Employee status updated successfully.",
      });

      await refreshEmployees(session.token);
    } catch (error) {
      setStatusMessage({
        type: "error",
        text: error.message,
      });
      handleProtectedError(error);
    } finally {
      setIsUpdatingEmployeeStatus(false);
    }
  }

  function handleOpenCreateDepartment() {
    clearSelectedEmployee();
    closeDesignationModal();
    setEditingDepartment(null);
    setDepartmentForm(INITIAL_DEPARTMENT_FORM);
    setIsDepartmentModalOpen(true);
  }

  function handleOpenEditDepartment(department) {
    clearSelectedEmployee();
    closeDesignationModal();
    setEditingDepartment(department);
    setDepartmentForm(createDepartmentFormFromDepartment(department));
    setIsDepartmentModalOpen(true);
  }

  async function handleSubmitDepartment(event) {
    event.preventDefault();
    if (!session?.token) return;

    try {
      setIsSavingDepartment(true);
      setStatusMessage(null);

      const payload = {
        name: departmentForm.name,
        description: departmentForm.description,
        departmentHeadName: departmentForm.departmentHeadName,
        departmentHeadTitle: departmentForm.departmentHeadTitle,
        status: departmentForm.status,
      };

      if (editingDepartment?._id) {
        await updateDepartment(session.token, editingDepartment._id, payload);
        setStatusMessage({
          type: "success",
          text: "Department updated successfully.",
        });
      } else {
        await createDepartment(session.token, payload);
        setStatusMessage({
          type: "success",
          text: "Department created successfully.",
        });
      }

      closeDepartmentModal();
      await Promise.all([
        refreshDepartments(session.token),
        refreshEmployees(session.token),
        refreshDesignations(session.token),
      ]);
    } catch (error) {
      setStatusMessage({
        type: "error",
        text: error.message,
      });
      handleProtectedError(error);
    } finally {
      setIsSavingDepartment(false);
    }
  }

  async function handleToggleDepartmentStatus(department) {
    if (!session?.token || !department?._id) return;

    const nextStatus = department.status === "active" ? "inactive" : "active";
    const confirmMessage =
      nextStatus === "inactive"
        ? `Mark ${department.name} as inactive?`
        : `Mark ${department.name} as active again?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setStatusMessage(null);
      await updateDepartmentStatus(session.token, department._id, nextStatus);
      setStatusMessage({
        type: "success",
        text: `Department ${nextStatus === "active" ? "activated" : "marked inactive"} successfully.`,
      });
      await refreshDepartments(session.token);
    } catch (error) {
      setStatusMessage({
        type: "error",
        text: error.message,
      });
      handleProtectedError(error);
    }
  }

  function handleOpenCreateDesignation() {
    clearSelectedEmployee();
    closeDepartmentModal();
    setEditingDesignation(null);
    setDesignationForm(INITIAL_DESIGNATION_FORM);
    setIsDesignationModalOpen(true);
  }

  function handleOpenEditDesignation(designation) {
    clearSelectedEmployee();
    closeDepartmentModal();
    setEditingDesignation(designation);
    setDesignationForm(createDesignationFormFromDesignation(designation));
    setIsDesignationModalOpen(true);
  }

  async function handleSubmitDesignation(event) {
    event.preventDefault();
    if (!session?.token) return;

    try {
      setIsSavingDesignation(true);
      setStatusMessage(null);

      const payload = {
        name: designationForm.name,
        department: designationForm.department,
        level: designationForm.level,
        description: designationForm.description,
        status: designationForm.status,
      };

      if (editingDesignation?._id) {
        await updateDesignation(session.token, editingDesignation._id, payload);
        setStatusMessage({
          type: "success",
          text: "Designation updated successfully.",
        });
      } else {
        await createDesignation(session.token, payload);
        setStatusMessage({
          type: "success",
          text: "Designation created successfully.",
        });
      }

      closeDesignationModal();
      await Promise.all([
        refreshDesignations(session.token),
        refreshEmployees(session.token),
        refreshDepartments(session.token),
      ]);
    } catch (error) {
      setStatusMessage({
        type: "error",
        text: error.message,
      });
      handleProtectedError(error);
    } finally {
      setIsSavingDesignation(false);
    }
  }

  async function handleToggleDesignationStatus(designation) {
    if (!session?.token || !designation?._id) return;

    const nextStatus = designation.status === "active" ? "inactive" : "active";
    const confirmMessage =
      nextStatus === "inactive"
        ? `Mark ${designation.name} as inactive?`
        : `Mark ${designation.name} as active again?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setStatusMessage(null);
      await updateDesignationStatus(session.token, designation._id, nextStatus);
      setStatusMessage({
        type: "success",
        text: `Designation ${nextStatus === "active" ? "activated" : "marked inactive"} successfully.`,
      });
      await refreshDesignations(session.token);
    } catch (error) {
      setStatusMessage({
        type: "error",
        text: error.message,
      });
      handleProtectedError(error);
    }
  }

  function handleLogout() {
    setIsLogoutConfirmOpen(true);
  }

  function handleCancelLogout() {
    setIsLogoutConfirmOpen(false);
  }

  function handleConfirmLogout() {
    persistSession(null);
    resetAdminShell();
    setStatusMessage(null);
    setIsLogoutConfirmOpen(false);
  }

  function downloadEmployeeCsv() {
    if (!filteredEmployees.length) {
      setStatusMessage({
        type: "error",
        text: "There are no employees to export.",
      });
      return;
    }

    const header = [
      "Employee ID",
      "Full Name",
      "Email",
      "Username",
      "Designation",
      "Department",
      "Joining Date",
      "Status",
      "Address",
    ];

    const rows = filteredEmployees.map((employee) => [
      employee.employeeId,
      employee.fullName,
      employee.email,
      employee.username,
      employee.designation,
      employee.department,
      employee.dateOfJoining,
      employee.status,
      buildEmployeeAddress(employee),
    ]);

    const csv = [header, ...rows]
      .map((row) =>
        row
          .map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`)
          .join(",")
      )
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "instateam-employees.csv";
    link.click();
    URL.revokeObjectURL(url);
  }

  function onSidebarSelect(key) {
    const matchedItem = SIDEBAR_ITEMS.find((item) => item.key === key);
    if (
      matchedItem?.permissionKey &&
      !canAccessAdminFeature(session?.admin?.permissions, matchedItem.permissionKey)
    ) {
      setStatusMessage({
        type: "warning",
        text: `You do not have access to ${matchedItem.label}.`,
      });
      return;
    }

    clearSidebarNotificationDot(matchedItem?.notificationCategory);

    if (key === "dashboard" || key === "tasks" || key === "reports" || key === "settings") {
      clearSelectedEmployee();
      setSelectedPerformanceEmployee(null);
      closeDepartmentModal();
      closeDesignationModal();
      setView(key);
      return;
    }

    if (key === "employees") {
      openEmployeesView();
      return;
    }

    if (key === "departments") {
      openDepartmentsView();
      return;
    }

    if (key === "designations") {
      openDesignationsView();
      return;
    }

    if (key === "attendance") {
      openAttendanceView();
      return;
    }

    if (key === "leaves") {
      openLeavesView();
      return;
    }

    if (key === "payroll") {
      openPayrollView();
      return;
    }

    if (key === "chat") {
      openChatView();
      return;
    }

    if (key === "complaints") {
      clearSelectedEmployee();
      closeDepartmentModal();
      closeDesignationModal();
      setView("complaints");
      return;
    }

    if (key === "announcements") {
      clearSelectedEmployee();
      closeDepartmentModal();
      closeDesignationModal();
      setView("announcements");
      return;
    }

    if (key === "documents") {
      clearSelectedEmployee();
      closeDepartmentModal();
      closeDesignationModal();
      setView("documents");
      return;
    }

    setStatusMessage({
      type: "warning",
      text: `${matchedItem?.label || "This page"} is not available right now.`,
    });
  }

  function jumpToPasswordSection() {
    document
      .getElementById("employee-password-card")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  return (
    <div className="admin-app-shell">
      {view === "login" ? (
        <AdminLoginPage
          loginForm={loginForm}
          isSubmittingLogin={isSubmittingLogin}
          showPassword={showPassword}
          onChange={setLoginForm}
          onSubmit={handleAdminLogin}
          onTogglePassword={() => setShowPassword((value) => !value)}
          statusMessage={statusMessage}
          onForgotPassword={() =>
            setStatusMessage({
              type: "warning",
              text: "PP",
            })
          }
        />
      ) : (
        <>
          <div className={`admin-portal-layout ${isSidebarOpen ? "" : "sidebar-collapsed"}`}>
            {isSidebarOpen ? (
              <Sidebar
                activeKey={activeSidebarKey}
                items={accessibleSidebarItems}
                onSelect={onSidebarSelect}
                onLogout={handleLogout}
                notificationCounts={notificationCounts}
              />
            ) : null}

            <main className="admin-portal-main">
              <Header
                title={headerTitle}
                subtitle={headerSubtitle}
                admin={session?.admin}
                onOpenProfile={openProfileView}
                onToggleSidebar={() => setIsSidebarOpen((current) => !current)}
                onOpenNotifications={() => {
                  setNotificationMode("list");
                  setView("notifications");
                }}
                showNotifications={canAccessAdminFeature(
                  session?.admin?.permissions,
                  "notifications"
                )}
                notificationCount={notificationCount}
                searchText={currentSearchText}
                onSearchChange={handleHeaderSearchChange}
                searchPlaceholder={currentSearchPlaceholder}
                searchDisabled={!isHeaderSearchEnabled}
              />

              {statusMessage ? (
                <StatusBanner
                  type={statusMessage.type}
                  text={statusMessage.text}
                  onClose={() => setStatusMessage(null)}
                />
              ) : null}

              {view === "dashboard" ? (
                <AdminDashboardPage
                  token={session?.token}
                  searchText={currentSearchText}
                  onNavigate={setView}
                  onNotify={setStatusMessage}
                  onProtectedError={handleProtectedError}
                />
              ) : null}

              {view === "profile" ? (
                <AdminProfilePage
                  token={session?.token}
                  sessionAdmin={session?.admin}
                  onSessionUpdate={(nextAdmin) =>
                    persistSession({
                      token: session?.token,
                      admin: nextAdmin,
                    })
                  }
                  onProtectedError={handleProtectedError}
                  onNotify={setStatusMessage}
                />
              ) : null}

              {view === "employees" ? (
                <EmployeesPage
                  employees={paginatedEmployees}
                  totalEmployees={totalEmployees}
                  activeEmployees={activeEmployees}
                  inactiveEmployees={inactiveEmployees}
                  newJoinings={newJoinings}
                  departmentFilter={employeeDepartmentFilter}
                  departmentOptions={employeeDepartmentOptions}
                  onDepartmentFilterChange={setEmployeeDepartmentFilter}
                  onAddEmployee={() => {
                    clearSelectedEmployee();
                    closeDepartmentModal();
                    closeDesignationModal();
                    setView("add-employee");
                  }}
                  onViewEmployee={openEmployeeDetails}
                  onExport={downloadEmployeeCsv}
                  payrollValue={payrollValue}
                  currentPage={safeEmployeePage}
                  totalPages={employeeTotalPages}
                  onPageChange={setEmployeeCurrentPage}
                  isLoadingEmployees={isLoadingEmployees}
                  resultsCount={filteredEmployees.length}
                  currencyFormatter={currencyFormatter}
                />
              ) : null}

            {view === "add-employee" ? (
              <AddEmployeePage
                mode="create"
                employeeForm={employeeForm}
                employeeFiles={employeeFiles}
                onFieldChange={setEmployeeForm}
                onFileChange={setEmployeeFiles}
                onCancel={openEmployeesView}
                onSubmit={handleCreateEmployee}
                isSavingEmployee={isSavingEmployee}
                showPasswordFields
                submitLabel="Save Employee"
                departmentOptions={assignableDepartmentOptions}
                designationOptions={assignableDesignationOptions}
              />
            ) : null}

            {view === "employee-details" ? (
              <EmployeeDetailsPage
                employee={selectedEmployee}
                isLoadingEmployee={isLoadingEmployeeDetails}
                isEditingEmployee={isEditingEmployee}
                employeeForm={employeeForm}
                employeeFiles={employeeFiles}
                onEmployeeFieldChange={setEmployeeForm}
                onEmployeeFileChange={setEmployeeFiles}
                onEmployeeSubmit={handleUpdateEmployee}
                isSavingEmployee={isSavingEmployee}
                onCancelEdit={handleCancelEditEmployee}
                onBack={openEmployeesView}
                onBeginEdit={handleStartEditEmployee}
                onToggleStatus={handleToggleEmployeeStatus}
                isUpdatingEmployeeStatus={isUpdatingEmployeeStatus}
                passwordForm={passwordForm}
                onPasswordFieldChange={setPasswordForm}
                onPasswordSubmit={handlePasswordUpdate}
                isSavingPassword={isSavingPassword}
                onJumpToPassword={jumpToPasswordSection}
                departmentOptions={assignableDepartmentOptions}
                designationOptions={assignableDesignationOptions}
              />
            ) : null}

            {view === "departments" ? (
              <DepartmentsPage
                departments={paginatedDepartments}
                employees={employees}
                metrics={departmentMetrics}
                searchText={departmentSearchText}
                onSearchChange={setDepartmentSearchText}
                statusFilter={departmentStatusFilter}
                onStatusFilterChange={setDepartmentStatusFilter}
                onClearFilters={() => {
                  setDepartmentSearchText("");
                  setDepartmentStatusFilter("all");
                }}
                onAddDepartment={handleOpenCreateDepartment}
                onEditDepartment={handleOpenEditDepartment}
                onToggleDepartmentStatus={handleToggleDepartmentStatus}
                currentPage={safeDepartmentPage}
                totalPages={departmentTotalPages}
                onPageChange={setDepartmentCurrentPage}
                pageSize={departmentPageSize}
                onPageSizeChange={setDepartmentPageSize}
                resultsCount={filteredDepartments.length}
                isLoadingDepartments={isLoadingDepartments}
                isDepartmentModalOpen={isDepartmentModalOpen}
                departmentForm={departmentForm}
                onDepartmentFormChange={setDepartmentForm}
                onCloseDepartmentModal={closeDepartmentModal}
                onSubmitDepartment={handleSubmitDepartment}
                isSavingDepartment={isSavingDepartment}
                editingDepartment={editingDepartment}
              />
            ) : null}

            {view === "designations" ? (
              <DesignationsPage
                designations={paginatedDesignations}
                metrics={designationMetrics}
                searchText={designationSearchText}
                onSearchChange={setDesignationSearchText}
                departmentFilter={designationDepartmentFilter}
                departmentOptions={designationDepartmentOptions}
                onDepartmentFilterChange={setDesignationDepartmentFilter}
                statusFilter={designationStatusFilter}
                onStatusFilterChange={setDesignationStatusFilter}
                onClearFilters={() => {
                  setDesignationSearchText("");
                  setDesignationDepartmentFilter("all");
                  setDesignationStatusFilter("all");
                }}
                onAddDesignation={handleOpenCreateDesignation}
                onEditDesignation={handleOpenEditDesignation}
                onToggleDesignationStatus={handleToggleDesignationStatus}
                currentPage={safeDesignationPage}
                totalPages={designationTotalPages}
                onPageChange={setDesignationCurrentPage}
                pageSize={designationPageSize}
                onPageSizeChange={setDesignationPageSize}
                resultsCount={filteredDesignations.length}
                isLoadingDesignations={isLoadingDesignations}
                isDesignationModalOpen={isDesignationModalOpen}
                designationForm={designationForm}
                onDesignationFormChange={setDesignationForm}
                onCloseDesignationModal={closeDesignationModal}
                onSubmitDesignation={handleSubmitDesignation}
                isSavingDesignation={isSavingDesignation}
                editingDesignation={editingDesignation}
              />
            ) : null}

            {view === "attendance" ? (
              <AttendancePage
                token={session?.token}
                departmentOptions={activeDepartmentOptions}
                searchText={attendanceSearchText}
                onSearchChange={setAttendanceSearchText}
                onProtectedError={handleProtectedError}
                onNotify={setStatusMessage}
              />
            ) : null}

            {view === "leaves" ? (
              <LeavesPage
                token={session?.token}
                employees={employees}
                departmentOptions={activeDepartmentOptions}
                searchText={leaveSearchText}
                onSearchChange={setLeaveSearchText}
                onProtectedError={handleProtectedError}
                onNotify={setStatusMessage}
              />
            ) : null}

            {view === "payroll" ? (
              <PayrollPage
                token={session?.token}
                departmentOptions={activeDepartmentOptions}
                searchText={payrollSearchText}
                onSearchChange={setPayrollSearchText}
                onProtectedError={handleProtectedError}
                onNotify={setStatusMessage}
              />
            ) : null}

            {view === "chat" ? (
              <AdminChatPage
                token={session?.token}
                searchText={chatSearchText}
                onSearchChange={setChatSearchText}
              />
            ) : null}

            {view === "complaints" ? (
              <ComplaintsPage
                token={session?.token}
                employees={employees}
                searchText={complaintSearchText}
                onSearchChange={setComplaintSearchText}
                onProtectedError={handleProtectedError}
                onNotify={setStatusMessage}
              />
            ) : null}

            {view === "notifications" ? (
              <NotificationsPage
                token={session?.token}
                mode={notificationMode}
                onModeChange={setNotificationMode}
                onUnreadChange={(counts) => {
                  setNotificationCounts(counts || {});
                  setNotificationCount(counts?.unread || 0);
                }}
              />
            ) : null}

            {view === "announcements" ? (
              <AnouncementsPage
                token={session?.token}
                employees={employees}
                departmentOptions={activeDepartmentOptions}
                searchText={announcementSearchText}
                onSearchChange={setAnnouncementSearchText}
                onProtectedError={handleProtectedError}
                onNotify={setStatusMessage}
              />
            ) : null}

            {view === "documents" ? (
              <DocumentsPage
                token={session?.token}
                departmentOptions={activeDepartmentOptions}
                searchText={documentSearchText}
                onSearchChange={setDocumentSearchText}
                onProtectedError={handleProtectedError}
                onNotify={setStatusMessage}
              />
            ) : null}

            {view === "tasks" ? (
              <TasksPage
                token={session?.token}
                searchText={taskSearchText}
                onSearchChange={setTaskSearchText}
                onProtectedError={handleProtectedError}
                onNotify={setStatusMessage}
              />
            ) : null}

            {view === "reports" ? (
              <ReportsPage
                token={session?.token}
                searchText={reportSearchText}
                onSearchChange={setReportSearchText}
                onNotify={setStatusMessage}
                onOpenEmployeePerformance={openEmployeePerformance}
              />
            ) : null}

            {view === "employee-performance" ? (
              <AdminEmployeePerformancePage
                token={session?.token}
                employee={selectedPerformanceEmployee}
                initialRange={selectedPerformanceFilters.range}
                initialMonth={selectedPerformanceFilters.month}
                onBack={() => setView("reports")}
              />
            ) : null}

            {view === "settings" ? (
              <AdminSettingsPage
                token={session?.token}
                sessionAdmin={session?.admin}
                onSessionUpdate={(nextAdmin) =>
                  persistSession({
                    token: session?.token,
                    admin: nextAdmin,
                  })
                }
                onProtectedError={handleProtectedError}
                onNotify={setStatusMessage}
              />
            ) : null}
            </main>
          </div>

          {isLogoutConfirmOpen ? (
            <div className="modal-overlay" role="presentation" onClick={handleCancelLogout}>
              <section
                className="employee-modal logout-confirm-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="admin-logout-confirm-title"
                onClick={(event) => event.stopPropagation()}
              >
                <div className="employee-modal-title">
                  <h3 id="admin-logout-confirm-title">Logout from Insta Team?</h3>
                  <p>Are you sure you want to logout from your admin panel?</p>
                </div>

                <div className="logout-confirm-actions">
                  <button type="button" className="secondary-button" onClick={handleCancelLogout}>
                    Cancel
                  </button>
                  <button type="button" className="primary-button" onClick={handleConfirmLogout}>
                    Yes, Logout
                  </button>
                </div>
              </section>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

export default function App() {
  const [isMobileRestricted, setIsMobileRestricted] = useState(() =>
    shouldRestrictToDesktop()
  );

  useEffect(() => {
    const updateRestrictionState = () => {
      setIsMobileRestricted(shouldRestrictToDesktop());
    };

    updateRestrictionState();
    window.addEventListener("resize", updateRestrictionState);
    window.addEventListener("orientationchange", updateRestrictionState);

    return () => {
      window.removeEventListener("resize", updateRestrictionState);
      window.removeEventListener("orientationchange", updateRestrictionState);
    };
  }, []);

  if (isMobileRestricted) {
    return <DeviceRestrictedScreen />;
  }

  return <DesktopApp />;
}
